import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, MapPin, Send, Loader2, Instagram, Facebook, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", tour_interest: "", travel_date: "", travelers: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone)) {
      toast.error("Please provide your name and at least email or phone");
      return;
    }
    setLoading(true);
    try {
      await api.createInquiry(form);
      toast.success("Inquiry sent! Lucky ji will reach out soon.");
      setForm({ name: "", email: "", phone: "", message: "", tour_interest: "", travel_date: "", travelers: "" });
    } catch (err) {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="orb orb-blue" style={{ width: 400, height: 400, top: 50, left: -100 }} />
      <div className="orb orb-indigo" style={{ width: 350, height: 350, bottom: 100, right: -100 }} />

      <section className="relative py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-14 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-5">
              <Sparkles className="w-3 h-3" /> START YOUR JOURNEY
            </div>
            <h1 className="font-display font-bold text-5xl lg:text-7xl text-[#0A1A3A] leading-[1.02]">
              Let's plan your<br/><span className="font-serif-italic font-medium text-gradient-blue">next escape</span>.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto font-serif-italic">Tell us your dream destination — we'll respond within 24 hours with a thoughtful, personalised plan.</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 glass-strong p-8 lg:p-10">
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required className="mt-1.5 rounded-xl bg-white/70 border-white/60 h-11" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" className="mt-1.5 rounded-xl bg-white/70 border-white/60 h-11" data-testid="contact-phone-input" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1.5 rounded-xl bg-white/70 border-white/60 h-11" data-testid="contact-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Tour Interest</Label>
                  <Select value={form.tour_interest} onValueChange={(v) => setForm({ ...form, tour_interest: v })}>
                    <SelectTrigger className="mt-1.5 rounded-xl bg-white/70 border-white/60 h-11" data-testid="contact-tour-interest-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic</SelectItem>
                      <SelectItem value="international">International</SelectItem>
                      <SelectItem value="group">Group Tour</SelectItem>
                      <SelectItem value="honeymoon">Honeymoon</SelectItem>
                      <SelectItem value="pilgrimage">Pilgrimage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Travel Date</Label>
                  <Input value={form.travel_date} onChange={(e) => setForm({ ...form, travel_date: e.target.value })} placeholder="e.g., May 2026" className="mt-1.5 rounded-xl bg-white/70 border-white/60 h-11" data-testid="contact-travel-date-input" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Travelers</Label>
                  <Input value={form.travelers} onChange={(e) => setForm({ ...form, travelers: e.target.value })} placeholder="e.g., 2 Adults, 1 Child" className="mt-1.5 rounded-xl bg-white/70 border-white/60 h-11" data-testid="contact-travelers-input" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Tell us about your dream trip</Label>
                  <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="The more details, the better!" className="mt-1.5 rounded-xl bg-white/70 border-white/60" data-testid="contact-message-input" />
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={loading} className="w-full btn-luxe rounded-2xl text-white h-12 text-base" data-testid="contact-submit-button">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Inquiry
                  </Button>
                </div>
              </form>
            </div>

            <div className="space-y-4">
              <div className="glass-strong p-7">
                <h3 className="font-display font-bold text-2xl text-[#0A1A3A]">Reach Lucky ji</h3>
                <div className="divider-dot mt-3 mb-5" />
                <ul className="space-y-5 text-sm">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 glass rounded-xl flex items-center justify-center shrink-0"><Phone className="w-4 h-4 text-[#1D4ED8]" /></div>
                    <div>
                      <div className="font-medium text-[#0A1A3A]">+91 97532 77771, +91 79871 52260</div>
                      <div className="text-xs text-slate-500 font-serif-italic">Mon–Sat · 9 AM – 8 PM</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 glass rounded-xl flex items-center justify-center shrink-0"><Mail className="w-4 h-4 text-[#1D4ED8]" /></div>
                    <div>
                      <div className="font-medium text-[#0A1A3A]">support@happyjourney4u.co.in</div>
                      <div className="text-xs text-slate-500 font-serif-italic">Reply within 24 hours</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 glass rounded-xl flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-[#1D4ED8]" /></div>
                    <div>
                      <div className="font-medium text-[#0A1A3A]">India</div>
                      <div className="text-xs text-slate-500 font-serif-italic">2 Sarthak Plaza Complex, Dakwadi, Burhanpur, MP 450331</div>
                    </div>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t border-white/60 flex gap-3">
                  <a href="#" aria-label="Instagram" className="w-11 h-11 glass rounded-xl flex items-center justify-center text-[#1D4ED8] hover:scale-105 transition-transform"><Instagram className="w-4 h-4" /></a>
                  <a href="#" aria-label="Facebook" className="w-11 h-11 glass rounded-xl flex items-center justify-center text-[#1D4ED8] hover:scale-105 transition-transform"><Facebook className="w-4 h-4" /></a>
                  <a href="https://wa.me/919753277771" target="_blank" rel="noopener noreferrer" className="w-11 h-11 glass rounded-xl flex items-center justify-center text-green-600 hover:scale-105 transition-transform"><MessageCircle className="w-4 h-4" /></a>
                </div>
              </div>

              <div className="glass-strong p-7 bg-gradient-to-br from-[#1E3A8A]/85 to-[#0EA5E9]/65 text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">— Promise</div>
                <h4 className="font-display font-bold text-2xl mt-2 leading-tight">No bots. No call centres.</h4>
                <p className="text-cyan-50 text-sm mt-2 font-serif-italic">Every inquiry is personally read and answered by Lucky ji.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
